import { useTranslation } from "react-i18next"

export const useTranslations = ({ selectedRowCount = 0 }: { selectedRowCount?: number } = {}): Translations => {
  const { t } = useTranslation()
  const { t: tOrg } = useTranslation("org")

  return {
    nameHeaderText: tOrg("orgMembershipsPage.nameHeaderText", {
      defaultValue: defaultTranslations.nameHeaderText,
    }),
    emailHeaderText: tOrg("orgMembershipsPage.emailHeaderText", {
      defaultValue: defaultTranslations.emailHeaderText,
    }),
    roleHeaderText: tOrg("orgMembershipsPage.roleHeaderText", {
      defaultValue: defaultTranslations.roleHeaderText,
    }),
    avatarHeaderText: tOrg("orgMembershipsPage.avatarHeaderText", {
      defaultValue: defaultTranslations.avatarHeaderText,
    }),
    actionsHeaderText: defaultTranslations.actionsHeaderText,
    invitationStatusHeaderText: tOrg("orgMembershipsPage.invitationStatusHeaderText", {
      defaultValue: defaultTranslations.invitationStatusHeaderText,
    }),
    noMembershipsText: tOrg("orgMembershipsPage.noMembershipsText", {
      defaultValue: defaultTranslations.noMembershipsText,
    }),
    ownerRoleText: tOrg("orgMembershipsPage.ownerRoleText", {
      defaultValue: defaultTranslations.ownerRoleText,
    }),
    memberRoleText: tOrg("orgMembershipsPage.memberRoleText", {
      defaultValue: defaultTranslations.memberRoleText,
    }),
    limitedMemberRoleText: tOrg("orgMembershipsPage.limitedMemberRoleText", {
      defaultValue: defaultTranslations.limitedMemberRoleText,
    }),
    noAccessText: tOrg("orgMembershipsPage.noAccessText", {
      defaultValue: defaultTranslations.noAccessText,
    }),
    managePermissionsButtonText: tOrg("orgMembershipsPage.managePermissionsButtonText", {
      defaultValue: defaultTranslations.managePermissionsButtonText,
    }),
    cancelButtonLabel: tOrg("orgMembershipsPage.cancelButtonLabel", {
      defaultValue: defaultTranslations.cancelButtonLabel,
    }),
    deactivateBtnLabel: tOrg("orgMembershipsPage.deactivateBtnLabel", {
      defaultValue: defaultTranslations.deactivateBtnLabel,
      count: selectedRowCount,
    }),
    deactivateDialogTitle: tOrg("orgMembershipsPage.deactivateDialogTitle", {
      defaultValue: defaultTranslations.deactivateDialogTitle,
    }),
    deactivateDialogText: tOrg("orgMembershipsPage.deactivateDialogText", {
      defaultValue: defaultTranslations.deactivateDialogText,
      count: selectedRowCount,
    }),
    activateBtnLabel: tOrg("orgMembershipsPage.activateBtnLabel", {
      defaultValue: defaultTranslations.activateBtnLabel,
      count: selectedRowCount,
    }),
    activateDialogTitle: tOrg("orgMembershipsPage.activateDialogTitle", {
      defaultValue: defaultTranslations.activateDialogTitle,
    }),
    activateDialogText: tOrg("orgMembershipsPage.activateDialogText", {
      defaultValue: defaultTranslations.activateDialogText,
      count: selectedRowCount,
    }),
    activeMembershipsOption: tOrg("orgMembershipsPage.activeMembershipsOption", {
      defaultValue: defaultTranslations.activeMembershipsOption,
    }),
    inactiveMembershipsOption: tOrg("orgMembershipsPage.inactiveMembershipsOption", {
      defaultValue: defaultTranslations.inactiveMembershipsOption,
    }),
    acceptedInvitationStatus: tOrg("orgMembershipsPage.acceptedInvitationStatus", {
      defaultValue: defaultTranslations.acceptedInvitationStatus,
    }),
    pendingInvitationStatus: tOrg("orgMembershipsPage.pendingInvitationStatus", {
      defaultValue: defaultTranslations.pendingInvitationStatus,
    }),
    rejectedInvitationStatus: tOrg("orgMembershipsPage.rejectedInvitationStatus", {
      defaultValue: defaultTranslations.rejectedInvitationStatus,
    }),
    notInvitedInvitationStatus: tOrg("orgMembershipsPage.notInvitedInvitationStatus", {
      defaultValue: defaultTranslations.notInvitedInvitationStatus,
    }),
    updatePermissionDialogHeaderText: tOrg("orgMembershipsPage.updatePermissionDialogHeaderText", {
      defaultValue: defaultTranslations.updatePermissionDialogHeaderText,
    }),
    reactivationInfo: tOrg("orgMembershipsPage.reactivationInfo", {
      defaultValue: defaultTranslations.reactivationInfo,
    }),
    deactivatedOnHeaderText: tOrg("orgMembershipsPage.deactivatedOnHeaderText", {
      defaultValue: defaultTranslations.deactivatedOnHeaderText,
    }),
    settings: t("settings", { defaultValue: defaultTranslations.settings }),
    filter: t("filter", { defaultValue: defaultTranslations.filter }),
    all: t("all", { defaultValue: defaultTranslations.all }),
    noData: t("noData", { defaultValue: defaultTranslations.noData }),
    invite: t("invite", { defaultValue: defaultTranslations.invite }),
  }
}

const defaultTranslations = {
  nameHeaderText: "Name",
  emailHeaderText: "Email",
  roleHeaderText: "Role",
  avatarHeaderText: "Avatar",
  actionsHeaderText: "Actions",
  invitationStatusHeaderText: "Invitation status",
  noMembershipsText: "There are no members in this organisation",
  ownerRoleText: "Owner",
  memberRoleText: "Standard member",
  limitedMemberRoleText: "Limited member",
  deactivatedOnHeaderText: "Deactivated",
  noAccessText: "No access",
  managePermissionsButtonText: "Manage permissions for selected members",
  cancelButtonLabel: "Cancel",
  deactivateBtnLabel: "Deactivate selected memberships",
  deactivateDialogTitle: "Deactivate memberships",
  deactivateDialogText:
    "By deactivating the selected members, their access to the content of this organization will be revoked. Please confirm if you want to proceed with deactivating the selected members",
  activateBtnLabel: "Activate selected memberships",
  activateDialogTitle: "Activate memberships",
  activateDialogText:
    "By activating the selected members, they will be given the access to the content of this organization. Please confirm if you want to proceed with activating the selected members",
  activeMembershipsOption: "Active memberships",
  inactiveMembershipsOption: "Inactive memberships",
  acceptedInvitationStatus: "Accepted",
  pendingInvitationStatus: "Pending",
  rejectedInvitationStatus: "Rejected",
  notInvitedInvitationStatus: "Not invited",
  updatePermissionDialogHeaderText: "Updating permissions of the selected members",
  reactivationInfo:
    "Member can not be re-activated before 30 days of deactivation. This member can be re-activated again on",
  settings: "Settings",
  filter: "Filter",
  all: "All",
  noData: "No data",
  invite: "Invite",
}

export type Translations = typeof defaultTranslations
